/* eslint-disable react/prop-types,no-plusplus,react/no-access-state-in-setstate */
// @flow
import React, { Component } from 'react';
import type { Node, Element } from 'react';
import ScrollLock from 'react-scrolllock';
import moment from 'moment';
import { compose } from 'ramda';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { omit, isEmpty, cloneDeep } from 'lodash';
import * as queryString from 'query-string';
import DatePicker from '../DatePicker/DatePicker';
import { todayTomorrowDateFormat } from '../../../helpers/date';
import { getQueryParam, restrictPastBooking } from '../../../helpers/utils';
import BookingDate from './BookingDate';
import SearchInput from '../Input/SearchInput';
import TabContainer from '../../Common/TabContainer';
import TabItem from '../../Common/TabItem';
import { host } from '../../../helpers/url';
import type { State } from '../../../stores/reducer';
import { updateUI } from '../../../stores/ui';
import { toggleFilterPanel as toggleFilterPanelAction } from '../../../stores/search';
import withAnalytics from '../../Decorators/withAnalytics';
import { defaultSearchDates } from '../../../helpers/date';

import '../../../../static/scss/components/searchForm.scss';

type Props = {
  autoLookup?: boolean,
  modifier: string,
  placeholder?: string,
  highlight?: boolean,
  browser: any,
  history: any,
  dates?: {
    arriving: Date,
    leaving: ?Date,
    monthlyDuration: ?string
  },
  value?: string,
  children?: Node | Element<any>,
  onSubmitCallback?: () => void | boolean,
  onDateChange?: () => void,
  initialCoords?: any,
  hideInput?: boolean,
  disableDefaultDates?: boolean,
  detached?: boolean,
  setRef?: () => void,
  cta?: string,
  invalidDatesCallback: () => void,
  updateUI?: () => void,
  name?: string,
  analytics: any,
  tabTheme?: string,
  partner?: string,
  showLessThanOneMonth?: boolean,
  filterPanelOpen?: boolean,
  toggleFilterPanel?: () => void,
  hasUpdatedFilterQuery: boolean
};

type SearchState = {
  type: string,
  value: string,
  coords: any,
  isValidInput: boolean,
  datePickerVisible: boolean,
  isDirty: boolean,
  dates: {
    arriving: Date,
    leaving: ?Date,
    monthlyDuration: ?string
  },
  validDates: boolean,
  disableDefaultDates: boolean,
  sourceParam: string,
  awinParam: string
};

export class SearchForm extends Component<Props, SearchState> {
  constructor(props: Props) {
    super(props);

    this.onSubmit = this.onSubmit.bind(this);
    this.onClickHandler = this.onClickHandler.bind(this);
    this.onNativeDatePickerChange = this.onNativeDatePickerChange.bind(this);
    this.onInputChangeHandler = this.onInputChangeHandler.bind(this);
    this.onDatePickerDateChange = this.onDatePickerDateChange.bind(this);
    this.placeCallback = this.placeCallback.bind(this);
    this.checkTime = this.checkTime.bind(this);
    this.setMonthlyDuration = this.setMonthlyDuration.bind(this);
    this.setIsMonthly = this.setIsMonthly.bind(this);

    const sourceParam = getQueryParam(props.location.search, 'source');
    const awinParam = getQueryParam(props.location.search, 'awc');

    const { value, dates, initialCoords, disableDefaultDates } = props;

    this.state = {
      type: 'single',
      value: value || '',
      coords: initialCoords,
      isValidInput: true,
      datePickerVisible: false,
      isDirty: false,
      dates: [
        {
          arriving: moment(dates.arriving).startOf('minute'),
          leaving: dates.leaving
            ? moment(dates.leaving).startOf('minute')
            : null,
          monthlyDuration: dates.monthlyDuration
        }
      ],
      validDates: true,
      disableDefaultDates,
      sourceParam,
      awinParam
    };
  }

  componentDidMount() {
    this.props.setRef(this);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.highlight && !this.state.value.length) {
      this.shakeInputForm(nextProps.highlight);
    }

    const { arriving, leaving, monthlyDuration } = nextProps.dates;

    if (
      arriving === this.props.dates.arriving &&
      leaving === this.props.dates.leaving &&
      monthlyDuration === this.props.dates.monthlyDuration
    ) {
      return;
    }

    this.setState({
      dates: [
        {
          arriving: moment(arriving).startOf('minute'),
          leaving: leaving ? moment(leaving).startOf('minute') : null,
          monthlyDuration
        }
      ]
    });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { browser, mobileReSearch, hasUpdatedFilterQuery } = this.props;
    const { hasUpdatedFilterQuery: oldHasUpdatedFilterQuery } = prevProps;
    const { coords: oldCoords } = prevState;
    const { coords } = this.state;

    const shouldFilterSearch =
      oldHasUpdatedFilterQuery !== hasUpdatedFilterQuery &&
      hasUpdatedFilterQuery === true;

    const shouldAutoSearch = mobileReSearch && !browser.greaterThan.extraSmall;
    const searchCoordsChanged =
      (oldCoords === null && coords !== null) ||
      (oldCoords &&
        coords &&
        'lat' in oldCoords &&
        'lat' in coords &&
        (oldCoords.lat !== coords.lat || oldCoords.lng !== coords.lng));

    if (shouldAutoSearch && searchCoordsChanged) {
      this.onSubmit();
      return;
    }

    if (shouldFilterSearch) {
      this.onSubmit();
      return;
    }

    this.autoSearchOnDateChange(prevProps);
  }

  autoSearchOnDateChange(prevProps: Props) {
    const { browser, mobileReSearch } = this.props;
    const shouldAutoSearch = mobileReSearch && !browser.greaterThan.extraSmall;

    if (!shouldAutoSearch) {
      return;
    }

    const {
      dates: {
        arriving: prevArriving,
        leaving: prevLeaving,
        monthlyDuration: prevMonthlyDuration
      }
    } = prevProps;

    const {
      dates: { arriving, leaving, monthlyDuration }
    } = this.props;

    const monthlyChanged = monthlyDuration !== prevMonthlyDuration;
    const arrivingChanged = !moment(prevArriving).isSame(arriving);
    const hadLeavingDateThatChanged =
      typeof leaving !== typeof prevLeaving ||
      (leaving !== null &&
        prevLeaving !== null &&
        !moment(prevLeaving).isSame(leaving));

    const datesChanged =
      monthlyChanged || arrivingChanged || hadLeavingDateThatChanged;

    if (datesChanged) {
      this.onSubmit();
    }
  }

  onSubmit(e, data = {}) {
    const { name, partner, filterPanelOpen, toggleFilterPanel } = this.props;

    if (this.props.noSubmit && e) {
      e.preventDefault();
    }

    if (isEmpty(this.state.coords)) {
      return this.shakeInputForm();
    }

    if (this.state.disableDefaultDates) {
      this.shakeDates();
      return this.highlightDates();
    }

    const formQuery = {};
    for (let i = 0; i < this.form.elements.length; i++) {
      const el = this.form.elements[i];
      if (!isEmpty(el.name)) {
        formQuery[el.name] = el.value;
      }
    }

    if (isEmpty(formQuery.q)) {
      formQuery.q = name;
    }

    const query = {
      ...formQuery,
      ...data
    };

    if (this.props.onSubmitCallback) {
      this.props.onSubmitCallback(query, this.state);
    }

    const updatedQuery = omit(query, [
      'start_date',
      'start_time',
      'end_date',
      'end_time',
      'monthly_duration'
    ]);

    const { arriving, leaving, monthlyDuration } = this.state.dates[0];

    updatedQuery.arriving = arriving.toISOString();

    if (leaving) {
      updatedQuery.leaving = leaving.toISOString();
    }

    if (monthlyDuration) {
      updatedQuery.monthly_duration = monthlyDuration;
    }

    if (filterPanelOpen) {
      toggleFilterPanel();
    }

    updatedQuery.coords = `${this.state.coords.lat},${this.state.coords.lng}`;

    const qs = queryString.stringify(updatedQuery);

    const partnerUrl = partner ? `/partner/${partner}` : '';
    this.props.history.push(`${partnerUrl}/search/?${qs}`);

    return false;
  }

  shakeInputForm(highlight = false) {
    this.setState({
      isValidInput: false,
      highlight
    });
    setTimeout(() => {
      this.setState({
        isValidInput: true,
        highlight: false
      });
      this.props.updateUI({
        animateSearchForm: false
      });
    }, 500);

    return false;
  }

  shakeDates(highlight = false) {
    this.setState({
      validDates: false,
      highlight
    });
    setTimeout(() => {
      this.setState({
        validDates: true,
        highlightDates: false
      });
      this.props.updateUI({
        animateDates: false
      });
    }, 500);

    return false;
  }

  highlightDates(enabled = true) {
    this.setState(
      {
        validDates: !enabled
      },
      () => {
        if (enabled && typeof this.props.invalidDatesCallback === 'function') {
          this.props.invalidDatesCallback();
        }
      }
    );
  }

  onClickHandler(target, shouldOpenNonNativePicker = true) {
    // Once the date component is interacted with allow the user to keep the default they have seen
    this.setState({
      disableDefaultDates: false
    });

    const { datePickerVisible } = this.state;

    if (datePickerVisible && typeof target === 'string') {
      if (target !== this.state.datePickerTarget) {
        this.setState({
          datePickerTarget: target
        });
        return;
      }

      this.setState({
        datePickerVisible: !datePickerVisible
      });
      return;
    }

    if (shouldOpenNonNativePicker) {
      this.setState({
        datePickerVisible: !datePickerVisible,
        datePickerTarget: target,
        disableDefaultDates: false,
        validDates: true
      });
      return;
    }

    this.setState({
      datePickerTarget: target
    });
  }

  onDatePickerDateChange(which, unit, value) {
    // TODO - take into account multi date
    const dates = cloneDeep(this.state.dates);

    if (unit === 'date') {
      ['year', 'month', 'date'].forEach(elem => {
        if (typeof value.get === 'function') {
          dates[0][which].set(elem, value.get(elem));
        }
      });
    } else {
      dates[0][which].set(unit, value);
    }

    this.handleDateChange(dates);
  }

  onNativeDatePickerChange(which, timestamp) {
    const dates = [...this.state.dates];
    dates[0][which] = moment(timestamp);
    if (dates[0].arriving.isSameOrBefore(moment().startOf('hour'))) {
      dates[0].arriving = moment()
        .hour(dates[0].arriving.hour())
        .minute(dates[0].arriving.minute());
    }

    this.setState({ dates }, () => {
      this.checkTime();
    });
  }

  setIsMonthly(isMonthly: boolean) {
    const { arriving } = defaultSearchDates;

    this.setState({
      dates: [
        {
          arriving,
          leaving: isMonthly ? null : arriving.clone().add(2, 'hours'),
          monthlyDuration: isMonthly ? '0' : null
        }
      ]
    });
  }

  handleDateChange(dates) {
    const { arriving, leaving, monthlyDuration } = dates[0];

    const updatedDates = [
      {
        arriving: arriving.clone().startOf('minute'),
        leaving: leaving ? leaving.clone().startOf('minute') : null,
        monthlyDuration: monthlyDuration || null
      }
    ];

    this.setState(
      {
        isDirty: true,
        disableDefaultDates: false,
        dates: updatedDates
      },
      () => {
        this.highlightDates(false);
        this.props.onDateChange(updatedDates);
      }
    );
  }

  setMonthlyDuration(duration) {
    const dates = cloneDeep(this.state.dates);
    dates[0].leaving = null;
    dates[0].monthlyDuration = duration;

    this.handleDateChange(dates);
  }

  onInputChangeHandler(value, meta) {
    // Handles address resolution from geo
    if ('coords' in meta) {
      this.setState({
        value,
        coords: meta.coords
      });
      if (this.props.onInputChangeHandler) {
        this.props.onInputChangeHandler(value, meta.coords);
      }
    } else {
      this.setState({
        value,
        coords: null
      });
      if (this.props.onInputChangeHandler) {
        this.props.onInputChangeHandler(value);
      }
    }
  }

  checkTime() {
    const { dates } = this.state;

    const { arriving } = dates[0];
    const { monthlyDuration } = dates[0];
    let { leaving } = dates[0];

    if (leaving && leaving.isSameOrBefore(arriving)) {
      leaving = arriving.clone().add(2, 'hours');

      const updatedDates = [
        {
          arriving: arriving.clone().startOf('minute'),
          leaving: leaving.clone().startOf('minute'),
          monthlyDuration
        }
      ];

      this.setState({ dates: updatedDates }, () => {
        this.handleDateChange(updatedDates);
      });

      return;
    }

    const bookingDates = [...dates];

    bookingDates[0].arriving = restrictPastBooking(bookingDates[0].arriving);

    this.handleDateChange(bookingDates);
  }

  placeCallback(state) {
    this.setState({
      coords: {
        lat: state.lat,
        lng: state.lng
      }
    });
  }

  generateClassnames() {
    const baseClassName = 'c-searchform';
    const classNames = [baseClassName];

    if (this.props.modifier) {
      classNames.push(`${baseClassName}--${this.props.modifier}`);
    }

    if (this.props.detached) {
      classNames.push(
        `${baseClassName}--detached ${baseClassName}--detachedFocused`
      );
    }

    if (this.props.detached) {
      classNames.push(`${baseClassName}--detached`);
    }

    if (this.props.detached && !this.props.disableDefaultDates) {
      classNames.push(`${baseClassName}--detachedFocused`);
    }

    return classNames.join(' ');
  }

  render() {
    const className = this.generateClassnames();

    const {
      autoLookup,
      value,
      placeholder,
      browser,
      initialCoords,
      hideInput,
      modifier,
      allowScroll,
      cta,
      analytics,
      tabTheme,
      children,
      showLessThanOneMonth,
      location
    } = this.props;

    const {
      isValidInput,
      highlight,
      disableDefaultDates,
      validDates,
      datePickerTarget,
      datePickerVisible,
      sourceParam,
      awinParam,
      dates
    } = this.state;

    const filterQueryString = getQueryParam(location.search, 'filters');

    const { monthlyDuration } = dates[0];
    const isMonthly = monthlyDuration !== null;
    const isMonthlyTab = isMonthly ? 1 : 0;

    const searchForm = (
      <form
        ref={el => {
          this.form = el;
        }}
        action={`${host()}/search/`}
        className={className}
        onSubmit={this.onSubmit}
      >
        <TabContainer
          active={isMonthlyTab}
          theme={tabTheme}
          onTabChange={index => {
            const dateRange = index === 0 ? 'daily' : 'monthly';
            analytics.track(`click:search-block:toggle-${dateRange}`);
            this.setIsMonthly(index === 1);
          }}
        >
          <TabItem
            tab={() => (
              <>
                <span className="c-tab__nav-tab__label">Hourly / Daily</span>
              </>
            )}
          />
          <TabItem
            tab={() => (
              <>
                <span className="c-tab__nav-tab__label">Monthly</span>
              </>
            )}
          />
        </TabContainer>
        <SearchInput
          autoLookup={autoLookup}
          onChangeHandler={this.onInputChangeHandler}
          value={value}
          isValid={isValidInput}
          highlight={highlight}
          placeholder={placeholder}
          browser={browser}
          propagateEvents
          initialCoords={initialCoords}
          placeCallback={this.placeCallback}
          isHidden={hideInput}
          showGeoLocation
        />
        {dates &&
          dates.map((res, idx) => {
            // this possibly can be done more effectively...
            if (modifier === 'single' && idx > 0) {
              return false;
            }

            const inputNameModifier = modifier === 'multi' ? '[]' : '';

            const dateFrom = disableDefaultDates
              ? false
              : todayTomorrowDateFormat(res.leaving, isMonthly, isMonthly);

            const dateTo = disableDefaultDates
              ? false
              : todayTomorrowDateFormat(res.arriving, isMonthly, isMonthly);

            return (
              <div
                key={idx}
                className={`c-booking-date__container ${
                  !validDates ? 'c-booking-date__container--invalid' : ''
                }`}
              >
                <BookingDate
                  dateFrom={dateFrom}
                  dateFromRaw={res.arriving}
                  datePickerTarget={datePickerTarget}
                  datePickerVisible={datePickerVisible}
                  dateTo={dateTo}
                  dateToRaw={res.leaving}
                  isValid={validDates}
                  modifier={modifier}
                  onClickHandler={this.onClickHandler}
                  onNativeDateChange={this.onNativeDatePickerChange}
                  browser={browser}
                  isMonthly={isMonthly}
                  monthlyDuration={monthlyDuration}
                  onMonthlyChange={this.setMonthlyDuration}
                  showLessThanOneMonth={showLessThanOneMonth}
                />
                <input
                  type="hidden"
                  name={`start_date${inputNameModifier}`}
                  value={res.arriving.format('DD MMM YYYY')}
                />
                {res.leaving !== null && (
                  <input
                    type="hidden"
                    name={`end_date${inputNameModifier}`}
                    value={res.leaving.format('DD MMM YYYY')}
                  />
                )}
                <input
                  type="hidden"
                  name={`monthly_duration${inputNameModifier}`}
                  value={res.monthlyDuration}
                />
                <input
                  type="hidden"
                  name={`start_time${inputNameModifier}`}
                  value={res.arriving.format('HH:mm:ss')}
                />
                {res.leaving !== null && (
                  <input
                    type="hidden"
                    name={`end_time${inputNameModifier}`}
                    value={res.leaving.format('HH:mm:ss')}
                  />
                )}
                {sourceParam && (
                  <input type="hidden" name="source" value={sourceParam} />
                )}
                {awinParam && (
                  <input type="hidden" name="awc" value={awinParam} />
                )}
                {modifier === 'multi' && (
                  <div className="c-searchform__bt-remove" />
                )}
                {filterQueryString && (
                  <input
                    type="hidden"
                    name="filters"
                    value={filterQueryString}
                  />
                )}
              </div>
            );
          })}
        {modifier === 'multi' && (
          <div className="c-searchform__add">Add another reservation</div>
        )}

        {children}

        {datePickerVisible && (
          <div className="c-datepicker-container">
            {!allowScroll && <ScrollLock />}
            <DatePicker
              onClose={this.onClickHandler}
              activeTab={datePickerTarget}
              onDateChange={this.onDatePickerDateChange}
              leaving={dates[0].leaving}
              arriving={dates[0].arriving}
              checkTime={this.checkTime}
              showTimePicker={!isMonthly}
              hideLeaving={isMonthly}
              isOpen={datePickerVisible}
            />
          </div>
        )}
        <button
          type="submit"
          className="c-searchform__submit"
          data-cy="searchform-submit"
        >
          {cta}
        </button>
      </form>
    );

    return searchForm;
  }
}

SearchForm.defaultProps = {
  placeholder: 'Where do you want to park?',
  children: null,
  highlight: false,
  onSubmitCallback: false,
  value: '',
  autoLookup: true,
  initialCoords: {},
  hideInput: false,
  disableDefaultDates: false,
  detached: false,
  dates: {
    arriving: defaultSearchDates.arriving.toDate(),
    leaving: defaultSearchDates.leaving.toDate(),
    monthlyDuration: null
  },
  cta: 'Search',
  name: '',
  tabTheme: '',
  partner: '',
  setRef: () => {},
  updateUI: () => {},
  onDateChange: () => {},
  toggleFilterPanel: () => {},
  showLessThanOneMonth: false,
  filterPanelOpen: false
};

const mapStateToProps = (state: State) => ({
  browser: state.browser,
  ui: state.ui,
  partner: state.brand.partner,
  features: state.features,
  hasUpdatedFilterQuery: state.search.hasUpdatedFilterQuery
});

const mapDispatchToProps = {
  updateUI,
  toggleFilterPanel: toggleFilterPanelAction
};

export default compose(
  withRouter,
  withAnalytics({}, {}),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(SearchForm);
